import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ApiError,
  centsToUSD,
  dens as densApi,
  finance as financeApi,
  type DenRow,
  type FinanceReport as Report,
  type ReportFilters,
} from '../api';
import { useUnits } from '../units';

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export function FinanceReport() {
  const { unitId = '' } = useParams();
  const { currentUnit, hasRole } = useUnits();
  const mayView = hasRole('ADMIN') || hasRole('PACK_COMMITTEE_FINANCE');

  const [filters, setFilters] = useState<ReportFilters>({});
  const [report, setReport] = useState<Report | null>(null);
  const [dens, setDens] = useState<DenRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    financeApi
      .report(unitId, filters)
      .then(setReport)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load the report'));
  }, [unitId, filters]);

  useEffect(load, [load]);
  useEffect(() => {
    densApi.list(unitId).then((r) => setDens(r.dens)).catch(() => setDens([]));
  }, [unitId]);

  const set = (patch: Partial<ReportFilters>) => setFilters((f) => ({ ...f, ...patch }));

  const scoutOptions = useMemo(
    () => (report?.ledger ?? []).map((l) => ({ id: l.scoutId, name: l.name })),
    [report],
  );

  if (!mayView) return <Navigate to="/" replace />;

  const csv = (section: 'ledger' | 'payments' | 'fundraisers') =>
    financeApi
      .reportCsv(unitId, filters, section)
      .then((t) => download(`finance-${section}.csv`, t))
      .catch(() => setError('Could not export CSV'));

  return (
    <div className="panel">
      <p className="muted small">
        <Link to={`/units/${unitId}/finance`}>← Finance</Link> · {currentUnit?.name}
      </p>
      <h1>Finance reporting</h1>

      <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'center', margin: '12px 0' }}>
        <label className="filter">
          Den{' '}
          <select value={filters.denId ?? ''} onChange={(e) => set({ denId: e.target.value || undefined, scoutId: undefined })}>
            <option value="">All</option>
            {dens.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        <label className="filter">
          Scout{' '}
          <select value={filters.scoutId ?? ''} onChange={(e) => set({ scoutId: e.target.value || undefined })}>
            <option value="">All</option>
            {scoutOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="filter">
          Fundraiser{' '}
          <select
            value={filters.fundraiserId ?? ''}
            onChange={(e) => set({ fundraiserId: e.target.value || undefined })}
          >
            <option value="">All</option>
            {(report?.fundraisers ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
        <label className="filter">
          From <input type="date" value={filters.from ?? ''} onChange={(e) => set({ from: e.target.value || undefined })} />
        </label>
        <label className="filter">
          To <input type="date" value={filters.to ?? ''} onChange={(e) => set({ to: e.target.value || undefined })} />
        </label>
        {(filters.denId || filters.scoutId || filters.from || filters.to || filters.fundraiserId) && (
          <button onClick={() => setFilters({})}>Clear</button>
        )}
      </div>

      {error && <p className="error">{error}</p>}
      {!report ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          <div className="tiles">
            <div className="tile">
              <div className="tile-num">{centsToUSD(report.dues.chargedCents)}</div>
              <div className="tile-label">Charged</div>
            </div>
            <div className="tile">
              <div className="tile-num">{centsToUSD(report.dues.paidCents)}</div>
              <div className="tile-label">Paid (all time)</div>
            </div>
            <div className="tile">
              <div className="tile-num">{centsToUSD(report.dues.creditsCents)}</div>
              <div className="tile-label">Fundraiser credits</div>
            </div>
            <div className="tile">
              <div className="tile-num">{centsToUSD(report.dues.outstandingCents)}</div>
              <div className="tile-label">Outstanding</div>
            </div>
            <div className="tile">
              <div className="tile-num">{centsToUSD(report.cash.collectedInRangeCents)}</div>
              <div className="tile-label">
                Collected{filters.from || filters.to ? ' (range)' : ''}
              </div>
            </div>
          </div>
          <p className="muted small">
            {report.scoutCount} Scout{report.scoutCount === 1 ? '' : 's'} in scope ·{' '}
            {report.cash.byMethod.map((m) => `${m.method} ${centsToUSD(m.cents)}`).join(' · ') || 'no payments in range'}
          </p>

          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h2>Per-Scout ledger</h2>
            <button onClick={() => csv('ledger')}>Export CSV</button>
          </div>
          <table className="grid">
            <thead>
              <tr>
                <th>Scout</th>
                <th>Den</th>
                <th>Charged</th>
                <th>Paid</th>
                <th>Credit</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {report.ledger.map((l) => (
                <tr key={l.scoutId}>
                  <td>{l.name}</td>
                  <td>{l.denName ?? '—'}</td>
                  <td>{centsToUSD(l.chargedCents)}</td>
                  <td>{centsToUSD(l.paidCents)}</td>
                  <td className={l.creditsCents ? '' : 'muted'}>
                    {l.creditsCents ? `−${centsToUSD(l.creditsCents)}` : '—'}
                  </td>
                  <td>
                    <strong className={l.balanceCents > 0 ? 'error' : ''}>{centsToUSD(l.balanceCents)}</strong>
                  </td>
                </tr>
              ))}
              {report.ledger.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted">
                    No Scouts in scope.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h2>Fundraising by campaign</h2>
            <button onClick={() => csv('fundraisers')}>Export CSV</button>
          </div>
          <table className="grid">
            <thead>
              <tr>
                <th>Fundraiser</th>
                <th>Sold</th>
                <th>Gross</th>
                <th>Commission</th>
                <th>Sellers</th>
              </tr>
            </thead>
            <tbody>
              {report.fundraisers.map((f) => (
                <tr key={f.id}>
                  <td>{f.name}</td>
                  <td>{f.itemsSold}</td>
                  <td>{centsToUSD(f.grossCents)}</td>
                  <td>{centsToUSD(f.commissionCents)}</td>
                  <td>{f.sellers}</td>
                </tr>
              ))}
              {report.fundraisers.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    No fundraisers.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <h2>Commission liability</h2>
          <div className="row" style={{ gap: 24, flexWrap: 'wrap' }}>
            <div>
              <h3>By den</h3>
              <table className="grid">
                <tbody>
                  {report.commissionByDen.map((d) => (
                    <tr key={d.denName}>
                      <td>{d.denName}</td>
                      <td>{centsToUSD(d.commissionCents)}</td>
                    </tr>
                  ))}
                  {report.commissionByDen.length === 0 && (
                    <tr>
                      <td className="muted">None.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div>
              <h3>By Scout</h3>
              <table className="grid">
                <tbody>
                  {report.commissionByScout.map((s) => (
                    <tr key={s.scoutId}>
                      <td>{s.name}</td>
                      <td className="muted small">{s.denName ?? '—'}</td>
                      <td>{centsToUSD(s.commissionCents)}</td>
                    </tr>
                  ))}
                  {report.commissionByScout.length === 0 && (
                    <tr>
                      <td className="muted">None.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="row" style={{ marginTop: 16 }}>
            <button onClick={() => csv('payments')}>Export payments CSV</button>
          </div>
        </>
      )}
    </div>
  );
}
